import type { BodensteckbriefEntry, BodensteckbriefFbfRow } from './listen.service';

/**
 * Bringt eine Bodensteckbrief-Liste auf die kanonische `schraffuren`-Objektform.
 *
 * Hintergrund (Lehmbodenform-Bug): Im gebuendelten D_Parser-Export wurden die
 * Schraffuren der Lehm-Steckbriefe (Substratgruppen L/tL/dL, Kapitel
 * "05Anhydro10Lehmböden") als ARRAY der schraffierten Spalten-IDs serialisiert
 * (`["3","9","10","11","12"]`) statt als Map `{ "3":"haken", ... }`.
 *
 * Der Renderer liest `f.schraffuren?.[col]` mit `col` = numerischer
 * Spalten-ID-String. Auf einem ARRAY ist `["3","9","10","11","12"]["3"]`
 * Array-Indexierung und liefert das ELEMENT an Index 3 = "11" — also eine ZAHL
 * statt eines Hakens. Genau diese Zahlen erschienen in der Schraffurtabelle.
 *
 * Diese Normalisierung wandelt jedes Array `[colId, ...]` in die Map
 * `{ colId: "haken", ... }` (das Array transportiert nur die schraffierten
 * Spalten, also ist "haken" der korrekte Zustand). Bereits korrekte Objekt-
 * Schraffuren (inkl. "x"/"?"/Freitext-Marker) bleiben unveraendert.
 *
 * Referenz-schonend: unveraenderte Eintraege/Listen werden per Referenz
 * durchgereicht (busted die Feinbodenform-Memoisierung nicht unnoetig).
 * Wird am Chokepoint `ListenService.getSync('Bodensteckbriefe')` aufgerufen —
 * dieselbe Stelle, die `Punktarten`/`Feinbodenformen` aus Alt-DB-Formen
 * reparariert. So zeigen Reader UND Editor sofort Haken statt Zahlen; beim
 * naechsten Speichern wird die Objekt-Form persistiert.
 */
export function normalizeBodensteckbriefe(
  list: BodensteckbriefEntry[] | undefined | null,
): BodensteckbriefEntry[] {
  if (!Array.isArray(list)) return [];

  let listChanged = false;
  const out = list.map(entry => {
    if (!entry || !Array.isArray(entry.feinbodenformen)) return entry;

    let entryChanged = false;
    const fbfs = entry.feinbodenformen.map((f: BodensteckbriefFbfRow) => {
      const s = f?.schraffuren as unknown;
      if (!Array.isArray(s)) return f; // bereits Objekt / null / fehlend -> unveraendert
      entryChanged = true;
      const map: Record<string, string> = {};
      for (const col of s) {
        const key = String(col ?? '').trim();
        if (key) map[key] = 'haken';
      }
      return { ...f, schraffuren: map };
    });

    if (!entryChanged) return entry;
    listChanged = true;
    return { ...entry, feinbodenformen: fbfs };
  });

  return listChanged ? out : list;
}
